import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { ClickableUser } from "../contexts/UserSheetContext";
import axios from "axios";
import { API } from "../App";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Calendar,
  Clock,
  Users,
  FileText,
  Bell,
  TrendingUp,
  ArrowRight,
  CheckCircle2,
  XCircle,
  AlertCircle,
  CalendarDays,
  CheckCheck,
  CalendarClock,
  CalendarX
} from "lucide-react";
import { toast } from "sonner";

const ROLES_FULL_ACCESS = ["admin", "hr"];

const SuiviBadge = ({ info }) => {
  if (info.status === "completed") {
    return (
      <span className="badge-approved inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border">
        <CheckCheck className="w-3 h-3" />
        Complété{info.date ? ` · ${info.date}` : ""}
      </span>
    );
  }
  if (info.status === "scheduled") {
    return (
      <span className="badge-pending inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border">
        <CalendarClock className="w-3 h-3" />
        Planifié{info.date ? ` · ${info.date}` : ""}
      </span>
    );
  }
  return (
    <span className="badge-rejected inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border">
      <CalendarX className="w-3 h-3" />
      Manquant
    </span>
  );
};

const getInitials = (name) => {
  const parts = (name || "").split(" ");
  return parts.map((p) => p[0]).join("").slice(0, 2).toUpperCase();
};

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentLeaves, setRecentLeaves] = useState([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [suiviEntretiens, setSuiviEntretiens] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const usersMap = useMemo(
    () => Object.fromEntries(allUsers.map((u) => [u.id, u])),
    [allUsers]
  );

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const promises = [
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/leaves`),
        axios.get(`${API}/meetings`),
        axios.get(`${API}/notifications`)
      ];

      const [statsRes, leavesRes, meetingsRes, notifRes] = await Promise.all(promises);

      setStats(statsRes.data);
      setRecentLeaves(leavesRes.data.slice(0, 5));
      setUpcomingMeetings(meetingsRes.data.filter(m => m.status === "scheduled").slice(0, 3));
      setNotifications(notifRes.data.filter(n => !n.is_read).slice(0, 5));

      if (ROLES_FULL_ACCESS.includes(user?.role)) {
        try {
          const suiviRes = await axios.get(`${API}/dashboard/suivi-entretiens`);
          setSuiviEntretiens(suiviRes.data);
        } catch (err) {
          console.error("Error fetching suivi entretiens:", err);
        }
      }

      if (!["employee", "collaborateur"].includes(user?.role)) {
        try {
          const usersRes = await axios.get(`${API}/users`);
          setAllUsers(usersRes.data);
        } catch (err) {
          console.error("Error fetching users:", err);
        }
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setIsLoading(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Bonjour";
    if (hour < 18) return "Bon après-midi";
    return "Bonsoir";
  };

  const getLeaveTypeLabel = (type) => {
    const labels = {
      conge_paye: "Congé payé",
      maladie: "Maladie",
      sans_solde: "Sans solde",
      rtt: "RTT",
      autre: "Autre"
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: "En attente", variant: "outline", className: "badge-pending" },
      approved: { label: "Approuvé", variant: "outline", className: "badge-approved" },
      rejected: { label: "Refusé", variant: "outline", className: "badge-rejected" }
    };
    const { label, className } = config[status] || config.pending;
    return <Badge className={className}>{label}</Badge>;
  };

  const handleApproveLeave = async (leaveId) => {
    try {
      await axios.put(`${API}/leaves/${leaveId}/review?action=approved`);
      toast.success("Congé approuvé");
      fetchDashboardData();
    } catch (error) {
      toast.error("Erreur lors de l'approbation");
    }
  };

  const handleRejectLeave = async (leaveId) => {
    try {
      await axios.put(`${API}/leaves/${leaveId}/review?action=rejected`);
      toast.success("Congé refusé");
      fetchDashboardData();
    } catch (error) {
      toast.error("Erreur lors du refus");
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-24 bg-gray-200 rounded-xl" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-gray-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: user?.role === "employee" ? "Mes congés" : "Congés en attente",
      value: user?.role === "employee" ? stats?.my_leaves : stats?.pending_leaves,
      icon: Calendar,
      color: "text-[#1A4D2E] dark:text-[#4CAF70]",
      bgColor: "bg-[#E8F5E9] dark:bg-[#1A4D2E]/20",
      link: "/leaves"
    },
    {
      title: "Réunions à venir",
      value: stats?.upcoming_meetings,
      icon: Users,
      color: "text-[#2563EB] dark:text-blue-400",
      bgColor: "bg-blue-50 dark:bg-blue-900/20",
      link: "/meetings"
    },
    {
      title: "Bulletins de paie",
      value: stats?.my_payslips,
      icon: FileText,
      color: "text-[#E07A5F] dark:text-orange-400",
      bgColor: "bg-orange-50 dark:bg-orange-900/20",
      link: "/payslips"
    },
    {
      title: user?.role === "hr" ? "Collaborateurs" : "Absences ce mois",
      value: user?.role === "hr" ? stats?.total_employees : stats?.total_absences_month,
      icon: user?.role === "hr" ? Users : Clock,
      color: "text-purple-600 dark:text-purple-400",
      bgColor: "bg-purple-50 dark:bg-purple-900/20",
      link: user?.role === "hr" ? "/users" : "/absences"
    }
  ];

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Welcome Section */}
      <div className="animate-fade-in">
        <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#111827] dark:text-[#D6E4F5]">
          {getGreeting()}, {user?.first_name} !
        </h1>
        <p className="text-[#4B5563] dark:text-[#7EA8CE] mt-2">
          Voici un résumé de votre activité RH.
        </p>
      </div>

      {/* Stats Grid - Bento Style */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card
              key={stat.title}
              className={`card-neumorphic cursor-pointer hover:-translate-y-1 transition-all duration-300 animate-fade-in stagger-${index + 1}`}
              onClick={() => navigate(stat.link)}
              data-testid={`stat-card-${index}`}
            >
              <CardContent className="p-4 md:p-6">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs md:text-sm text-[#4B5563] dark:text-[#7EA8CE] leading-tight">{stat.title}</p>
                    <p className="text-2xl md:text-3xl font-bold text-[#111827] dark:text-[#D6E4F5] mt-1 md:mt-2">{stat.value || 0}</p>
                  </div>
                  <div className={`p-2 md:p-3 rounded-xl ${stat.bgColor} shrink-0`}>
                    <Icon className={`w-5 h-5 md:w-6 md:h-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Leaves - Spans 2 columns */}
        <Card className="lg:col-span-2 card-neumorphic animate-fade-in">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="font-['Playfair_Display'] text-xl dark:text-[#D6E4F5]">
              {user?.role === "employee" ? "Mes demandes de congés" : "Demandes de congés récentes"}
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/leaves")}
              className="text-[#1A4D2E] hover:text-[#143D24] hover:bg-[#E8F5E9] dark:text-[#4B8BF4] dark:hover:text-[#6BA3F5] dark:hover:bg-[#0D1E3A]"
              data-testid="view-all-leaves"
            >
              Voir tout <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {recentLeaves.length === 0 ? (
              <div className="text-center py-8 text-[#9CA3AF] dark:text-[#3D607E]">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Aucune demande de congé</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentLeaves.map((leave, index) => (
                  <div
                    key={leave.id}
                    className={`flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-[#112240] hover:bg-gray-100 dark:hover:bg-[#1A3050]/60 transition-all animate-fade-in stagger-${index + 1}`}
                  >
                    <div className="flex items-center gap-3">
                      {user?.role !== "employee" && user?.role !== "collaborateur" ? (() => {
                        const u = usersMap[leave.user_id];
                        return (
                          <Avatar className="w-10 h-10 shrink-0">
                            <AvatarImage src={u?.avatar_url} />
                            <AvatarFallback className="bg-[#1A4D2E]/10 text-[#1A4D2E] dark:bg-[#1A4D2E]/30 dark:text-[#4CAF70] text-sm font-semibold">
                              {getInitials(leave.user_name)}
                            </AvatarFallback>
                          </Avatar>
                        );
                      })() : (
                        <div className="w-10 h-10 rounded-full bg-[#E8F5E9] dark:bg-[#1A4D2E]/30 flex items-center justify-center shrink-0">
                          <CalendarDays className="w-5 h-5 text-[#1A4D2E] dark:text-[#4CAF70]" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-[#111827] dark:text-[#D6E4F5]">
                          {user?.role !== "employee" && user?.role !== "collaborateur" && <><ClickableUser userId={leave.user_id}>{leave.user_name}</ClickableUser>{" — "}</>}
                          {getLeaveTypeLabel(leave.leave_type)}
                        </p>
                        <p className="text-sm text-[#4B5563] dark:text-[#7EA8CE]">
                          {leave.start_date} → {leave.end_date}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(leave.status)}
                      {(user?.role === "hr" || user?.role === "manager") && leave.status === "pending" && (
                        <div className="flex gap-1 ml-2">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={(e) => { e.stopPropagation(); handleApproveLeave(leave.id); }}
                            data-testid={`approve-leave-${leave.id}`}
                          >
                            <CheckCircle2 className="w-5 h-5" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={(e) => { e.stopPropagation(); handleRejectLeave(leave.id); }}
                            data-testid={`reject-leave-${leave.id}`}
                          >
                            <XCircle className="w-5 h-5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card className="card-neumorphic animate-fade-in">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="font-['Playfair_Display'] text-xl dark:text-[#D6E4F5]">Notifications</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/notifications")}
              className="text-[#1A4D2E] hover:text-[#143D24] hover:bg-[#E8F5E9] dark:text-[#4B8BF4] dark:hover:text-[#6BA3F5] dark:hover:bg-[#0D1E3A]"
              data-testid="view-all-notifications"
            >
              Voir tout <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {notifications.length === 0 ? (
              <div className="text-center py-8 text-[#9CA3AF] dark:text-[#3D607E]">
                <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Aucune nouvelle notification</p>
              </div>
            ) : (
              <div className="space-y-3">
                {notifications.map((notif, index) => (
                  <div
                    key={notif.id}
                    className={`p-3 rounded-xl bg-[#E8F5E9]/50 dark:bg-[#112240] border border-[#1A4D2E]/10 dark:border-[#1A3050] animate-fade-in stagger-${index + 1}`}
                  >
                    <p className="font-medium text-sm text-[#111827] dark:text-[#D6E4F5]">{notif.title}</p>
                    <p className="text-xs text-[#4B5563] dark:text-[#7EA8CE] mt-1 line-clamp-2">{notif.message}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Meetings */}
      <Card className="card-neumorphic animate-fade-in">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="font-['Playfair_Display'] text-xl dark:text-[#D6E4F5]">Réunions à venir</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/meetings")}
            className="text-[#1A4D2E] hover:text-[#143D24] hover:bg-[#E8F5E9] dark:text-[#4B8BF4] dark:hover:text-[#6BA3F5] dark:hover:bg-[#0D1E3A]"
            data-testid="view-all-meetings"
          >
            Voir tout <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {upcomingMeetings.length === 0 ? (
            <div className="text-center py-8 text-[#9CA3AF] dark:text-[#3D607E]">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucune réunion planifiée</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {upcomingMeetings.map((meeting, index) => (
                <div
                  key={meeting.id}
                  className={`p-4 rounded-xl bg-gradient-to-br from-[#1A4D2E] to-[#143D24] text-white animate-fade-in stagger-${index + 1}`}
                >
                  <p className="font-medium">{meeting.title}</p>
                  <p className="text-sm text-white/70 mt-1">{meeting.meeting_type}</p>
                  <div className="flex items-center gap-2 mt-3 text-sm text-white/80">
                    <CalendarDays className="w-4 h-4" />
                    <span>{meeting.date} à {meeting.time}</span>
                  </div>
                  {meeting.location && (
                    <p className="text-xs text-white/60 mt-2">{meeting.location}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Suivi des entretiens — HR/Admin uniquement */}
      {ROLES_FULL_ACCESS.includes(user?.role) && (
        <Card className="card-neumorphic animate-fade-in" data-testid="suivi-entretiens">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="font-['Playfair_Display'] text-xl dark:text-[#D6E4F5]">
              Suivi des entretiens
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/meetings")}
              className="text-[#1A4D2E] hover:text-[#143D24] hover:bg-[#E8F5E9] dark:text-[#4B8BF4] dark:hover:text-[#6BA3F5] dark:hover:bg-[#0D1E3A]"
            >
              Voir les réunions <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {suiviEntretiens.length === 0 ? (
              <div className="text-center py-8 text-[#9CA3AF] dark:text-[#3D607E]">
                <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Aucun collaborateur trouvé</p>
              </div>
            ) : (
              <>
                {/* Mobile : cartes */}
                <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-700/50">
                  {suiviEntretiens.slice(0, 20).map((row) => (
                    <div key={row.user_id} className="p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        {(() => {
                          const u = usersMap[row.user_id];
                          return (
                            <Avatar className="w-8 h-8 shrink-0">
                              <AvatarImage src={u?.avatar_url} />
                              <AvatarFallback className="text-xs bg-[#1A4D2E]/10 text-[#1A4D2E] dark:bg-[#1A4D2E]/30 dark:text-[#4CAF70]">
                                {getInitials(row.user_name)}
                              </AvatarFallback>
                            </Avatar>
                          );
                        })()}
                        <div>
                          <p className="font-medium text-[#111827] dark:text-[#D6E4F5]"><ClickableUser userId={row.user_id}>{row.user_name}</ClickableUser></p>
                          {row.department && (
                            <p className="text-xs text-[#9CA3AF] dark:text-[#7EA8CE] mt-0.5">{row.department}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-[#4B5563] dark:text-[#7EA8CE]">Annuel</span>
                          <SuiviBadge info={row.annuel} />
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-[#4B5563] dark:text-[#7EA8CE]">Trimestriel</span>
                          <SuiviBadge info={row.trimestriel} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop : tableau */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 dark:border-gray-700">
                        <th className="text-left py-3 px-4 font-semibold text-[#4B5563] dark:text-[#7EA8CE]">Collaborateur</th>
                        <th className="text-left py-3 px-4 font-semibold text-[#4B5563] dark:text-[#7EA8CE]">Département</th>
                        <th className="text-left py-3 px-4 font-semibold text-[#4B5563] dark:text-[#7EA8CE]">Entretien annuel</th>
                        <th className="text-left py-3 px-4 font-semibold text-[#4B5563] dark:text-[#7EA8CE]">Entretien trimestriel</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                      {suiviEntretiens.slice(0, 20).map((row) => (
                        <tr key={row.user_id} className="hover:bg-gray-50 dark:hover:bg-[#112240]/50 transition-colors">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              {(() => {
                                const u = usersMap[row.user_id];
                                return (
                                  <Avatar className="w-8 h-8 shrink-0">
                                    <AvatarImage src={u?.avatar_url} />
                                    <AvatarFallback className="text-xs bg-[#1A4D2E]/10 text-[#1A4D2E] dark:bg-[#1A4D2E]/30 dark:text-[#4CAF70]">
                                      {getInitials(row.user_name)}
                                    </AvatarFallback>
                                  </Avatar>
                                );
                              })()}
                              <span className="font-medium text-[#111827] dark:text-[#D6E4F5]"><ClickableUser userId={row.user_id}>{row.user_name}</ClickableUser></span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-[#4B5563] dark:text-[#7EA8CE]">{row.department || "—"}</td>
                          <td className="py-3 px-4"><SuiviBadge info={row.annuel} /></td>
                          <td className="py-3 px-4"><SuiviBadge info={row.trimestriel} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {suiviEntretiens.length > 20 && (
                  <p className="text-xs text-[#9CA3AF] text-center py-3 border-t border-gray-50 dark:border-gray-700/50">
                    Affichage des 20 premiers sur {suiviEntretiens.length} collaborateurs
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardPage;
